import * as React from 'react';

import { cn } from '@rematter/ui-web/lib';

import { VariableRecord } from '../../types/pricing';

import { PriceField } from './PriceField';

const VARIABLE_REGEX = /(\$[a-zA-Z_][a-zA-Z0-9_]*)/g;

type FormulaBarProps = {
  value: string | null;
  evaluatedValue: number | null;
  fieldLabel: string | null;
  className?: string;
  onChange?: (value: string) => void;
  variables?: VariableRecord[];
  isDisabled?: boolean;
};

const priceFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
  useGrouping: false,
});

const FIELD_LABELS: Record<string, string> = {
  targetPrice: 'Target',
  minPrice: 'Min',
  maxPrice: 'Max',
};

//
// ⚛️ ⚛️ ⚛️ FORMULA BAR ⚛️ ⚛️ ⚛️
//
export const FormulaBar = ({
  value,
  evaluatedValue,
  fieldLabel,
  className,
  onChange,
  variables,
  isDisabled,
}: FormulaBarProps) => {
  // Memoized values
  const highlighted = React.useMemo(() => {
    if (!value || onChange) return null;
    const parts = value.split(VARIABLE_REGEX);
    return parts.map((part, i) => {
      if (VARIABLE_REGEX.test(part)) {
        VARIABLE_REGEX.lastIndex = 0;
        return (
          <span key={i} className="font-semibold text-primary">
            {part}
          </span>
        );
      }
      return <span key={i}>{part}</span>;
    });
  }, [value, onChange]);

  return (
    <div
      className={cn(
        '@FormulaBar flex h-[38px] items-center gap-3 border border-neutral/20 bg-white px-3 py-0.5 text-sm',
        className,
      )}
    >
      <span className="shrink-0 rounded bg-primary px-2 text-base font-medium text-white italic">
        fx
      </span>
      {fieldLabel && (
        <span className="shrink-0 text-xs font-medium text-neutral/50 uppercase">
          {FIELD_LABELS[fieldLabel] ?? fieldLabel}
        </span>
      )}
      {value != null && onChange && variables && (
        <PriceField
          value={value}
          onChange={onChange}
          variables={variables}
          isDisabled={isDisabled}
          className="flex-1 border-none bg-transparent font-mono"
        />
      )}
      {value != null && !(onChange && variables) && (
        <span className="font-mono">{highlighted}</span>
      )}
      {value == null && (
        <span className="font-mono text-neutral/40">&nbsp;</span>
      )}
      {value != null && evaluatedValue != null && (
        <span className="shrink-0 text-neutral/60">
          = {priceFormatter.format(evaluatedValue)}
        </span>
      )}
    </div>
  );
};
